import React, { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'

type QrPoint = {
  id: string
  code: string
  label: string | null
  area_type: string | null
  floor: string | null
  status: string
  assigned_at: string | null
}

type Scan = {
  id: string
  scanned_at: string
  qr_point_id: string
}

interface PropertyQrPointsPanelProps {
  propertyId: string
}

export function PropertyQrPointsPanel({ propertyId }: PropertyQrPointsPanelProps) {
  const [points, setPoints] = useState<QrPoint[]>([])
  const [scans, setScans] = useState<Scan[]>([])
  const [manualCode, setManualCode] = useState('')
  const [label, setLabel] = useState('')
  const [areaType, setAreaType] = useState('other')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const load = async () => {
    setError(null)
    try {
      const [{ data: pts, error: pErr }, { data: sc, error: sErr }] = await Promise.all([
        supabase
          .from('qr_points')
          .select('id, code, label, area_type, floor, status, assigned_at')
          .eq('property_id', propertyId)
          .neq('status', 'unassigned')
          .order('label'),
        supabase
          .from('qr_scans')
          .select('id, scanned_at, qr_point_id, qr_points!inner(property_id)')
          .eq('qr_points.property_id', propertyId)
          .order('scanned_at', { ascending: false })
          .limit(30),
      ])
      if (pErr) throw pErr
      if (sErr) {
        // Fallback without join filter
        setScans([])
      } else {
        setScans((sc as Scan[]) || [])
      }
      setPoints((pts as QrPoint[]) || [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load QR points')
    }
  }

  useEffect(() => {
    if (propertyId) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId])

  const deactivate = async (id: string) => {
    setSaving(true)
    try {
      const { error: err } = await supabase.from('qr_points').update({ status: 'inactive' }).eq('id', id)
      if (err) throw err
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to deactivate')
    } finally {
      setSaving(false)
    }
  }

  const assignManual = async () => {
    if (!manualCode.trim() || !label.trim()) return
    setSaving(true)
    setError(null)
    try {
      const { error: err } = await supabase.rpc('assign_qr_point', {
        p_code: manualCode.trim().replace(/^RBCQR:/i, ''),
        p_property_id: propertyId,
        p_label: label.trim(),
        p_area_type: areaType,
        p_floor: null,
        p_notes: null,
      })
      if (err) throw err
      setManualCode('')
      setLabel('')
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Assign failed')
    } finally {
      setSaving(false)
    }
  }

  const lastScan = (pointId: string) =>
    scans.find((s) => s.qr_point_id === pointId)?.scanned_at || null

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">QR points</h3>
        <button
          type="button"
          onClick={load}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          Refresh
        </button>
      </div>
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
      )}

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium text-gray-700">Manual assign (fallback)</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            placeholder="Sticker code"
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Label"
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <select
            value={areaType}
            onChange={(e) => setAreaType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            {['roof', 'basement', 'compactor', 'hallway', 'other'].map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={saving}
            onClick={assignManual}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            Assign
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Label</th>
              <th className="text-left px-4 py-2 font-medium">Code</th>
              <th className="text-left px-4 py-2 font-medium">Area</th>
              <th className="text-left px-4 py-2 font-medium">Last scan</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {points.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-gray-500 text-center">
                  No QR stickers assigned to this property.
                </td>
              </tr>
            ) : (
              points.map((p) => {
                const last = lastScan(p.id)
                return (
                  <tr key={p.id} className="border-t border-gray-100">
                    <td className="px-4 py-2 font-medium text-gray-900">{p.label || '—'}</td>
                    <td className="px-4 py-2 font-mono text-xs">{p.code}</td>
                    <td className="px-4 py-2">{p.area_type || '—'}</td>
                    <td className="px-4 py-2 text-gray-600">
                      {last ? new Date(last).toLocaleString() : 'Never'}
                    </td>
                    <td className="px-4 py-2">{p.status}</td>
                    <td className="px-4 py-2 text-right">
                      {p.status === 'active' && (
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => deactivate(p.id)}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {scans.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">Recent scans</p>
          <ul className="text-sm text-gray-600 space-y-1">
            {scans.slice(0, 10).map((s) => (
              <li key={s.id}>{new Date(s.scanned_at).toLocaleString()}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default PropertyQrPointsPanel
